import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
// Spacing translation helper
const SPACING_MAP = {
    xs: '0.277rem',
    sm: '0.523rem',
    md: '0.923rem',
    lg: '1.308rem',
    xl: '1.962rem',
    xxl: '2.615rem',
    xxxl: '3.269rem',
};
const translateValue = (value) => {
    if (value === null || value === undefined)
        return '0';
    if (value === 'auto')
        return 'auto';
    if (typeof value === 'number')
        return `${value / 13}rem`;
    return SPACING_MAP[value] || '0';
};
const translateSpacing = (values) => {
    if (!values)
        return null;
    if (typeof values === 'string') {
        if (values.includes(' ') || values.includes('px'))
            return values;
        if (SPACING_MAP[values])
            return SPACING_MAP[values];
        return values;
    }
    if (Array.isArray(values)) {
        return values.map(translateValue).join(' ');
    }
    return null;
};
const sizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-md',
    lg: 'text-lg',
    xl: 'text-xl',
    xxl: 'text-2xl',
    xxxl: 'text-3xl',
};
const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
};
export const H1 = ({ children, className = '', color, 'data-testid': dataTestId, size, style: customStyle }) => {
    const style = { ...customStyle };
    if (color)
        style.color = color;
    const classes = [
        'heading',
        'heading-h1',
        size ? sizeClasses[size] : '',
        className,
    ].filter(Boolean).join(' ');
    return (_jsx("h1", { className: classes, "data-testid": dataTestId, style: Object.keys(style).length > 0 ? style : undefined, children: children }));
};
export const H2 = React.forwardRef((props, ref) => {
    const { children, className = '', color, 'data-testid': dataTestId, size } = props;
    const style = {};
    if (color)
        style.color = color;
    const classes = [
        'heading',
        'heading-h2',
        size ? sizeClasses[size] : '',
        className,
    ].filter(Boolean).join(' ');
    return (_jsx("h2", { className: classes, "data-testid": dataTestId, ref: ref, style: Object.keys(style).length > 0 ? style : undefined, children: children }));
});
H2.displayName = 'H2';
export const H3 = ({ children, className = '', color, 'data-testid': dataTestId, size }) => {
    const style = {};
    if (color)
        style.color = color;
    const classes = [
        'heading',
        'heading-h3',
        size ? sizeClasses[size] : '',
        className,
    ].filter(Boolean).join(' ');
    return (_jsx("h3", { className: classes, "data-testid": dataTestId, style: Object.keys(style).length > 0 ? style : undefined, children: children }));
};
export const H4 = ({ bold, children, className = '', color, 'data-testid': dataTestId, size }) => {
    const style = {};
    if (color)
        style.color = color;
    const classes = [
        'heading',
        'heading-h4',
        size ? sizeClasses[size] : '',
        bold ? 'font-bold' : '',
        className,
    ].filter(Boolean).join(' ');
    return (_jsx("h4", { className: classes, "data-testid": dataTestId, style: Object.keys(style).length > 0 ? style : undefined, children: children }));
};
export const H5 = ({ children, className = '', color, 'data-testid': dataTestId, size }) => {
    const style = {};
    if (color)
        style.color = color;
    const classes = [
        'heading',
        'heading-h5',
        size ? sizeClasses[size] : '',
        className,
    ].filter(Boolean).join(' ');
    return (_jsx("h5", { className: classes, "data-testid": dataTestId, style: Object.keys(style).length > 0 ? style : undefined, children: children }));
};
export const H6 = ({ children, className = '', color, 'data-testid': dataTestId, size }) => {
    const style = {};
    if (color)
        style.color = color;
    const classes = [
        'heading',
        'heading-h6',
        size ? sizeClasses[size] : '',
        className,
    ].filter(Boolean).join(' ');
    return (_jsx("h6", { className: classes, "data-testid": dataTestId, style: Object.keys(style).length > 0 ? style : undefined, children: children }));
};
export const Text = ({ align = 'left', bold, children, className = '', color, 'data-testid': dataTestId, decoration, padding, size = 'md', style: customStyle, }) => {
    const classes = [
        'text-body',
        sizeClasses[size],
        alignClasses[align],
        bold ? 'font-bold' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');
    const style = { ...customStyle };
    if (color)
        style.color = color;
    if (decoration)
        style.textDecoration = decoration;
    const paddingValue = translateSpacing(padding);
    if (paddingValue)
        style.padding = paddingValue;
    return (_jsx("p", { className: classes, "data-testid": dataTestId, style: Object.keys(style).length > 0 ? style : undefined, children: children }));
};
export const Span = ({ align = 'left', bold, children, className = '', color, 'data-testid': dataTestId, decoration, padding, size = 'md', }) => {
    const classes = [
        'text-body',
        sizeClasses[size],
        alignClasses[align],
        bold ? 'font-bold' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');
    const style = {};
    if (color)
        style.color = color;
    if (decoration)
        style.textDecoration = decoration;
    const paddingValue = translateSpacing(padding);
    if (paddingValue)
        style.padding = paddingValue;
    return (_jsx("span", { className: classes, "data-testid": dataTestId, style: Object.keys(style).length > 0 ? style : undefined, children: children }));
};
export const Strong = ({ align = 'left', children, className = '', color, 'data-testid': dataTestId, decoration, padding, size = 'md', }) => {
    const classes = [
        'text-body',
        sizeClasses[size],
        alignClasses[align],
        'font-bold',
        className,
    ]
        .filter(Boolean)
        .join(' ');
    const style = {};
    if (color)
        style.color = color;
    if (decoration)
        style.textDecoration = decoration;
    const paddingValue = translateSpacing(padding);
    if (paddingValue)
        style.padding = paddingValue;
    return (_jsx("b", { className: classes, "data-testid": dataTestId, style: Object.keys(style).length > 0 ? style : undefined, children: children }));
};
//# sourceMappingURL=typography.js.map